import {
  type CSSProperties,
  createContext,
  type ReactNode,
  useContext,
} from "react";

import {
  DEFAULT_REPORT_THEME,
  REPORT_CSS_VAR_PRIMARY,
  type ReportTheme,
} from "@/components/ui/report/reportTheme";
import { cn } from "@/lib/utils";

const ReportThemeContext = createContext<ReportTheme | null>(null);

/** Resolved report theme from the nearest {@link ReportThemeProvider}, or `null`. */
export const useReportTheme = (): ReportTheme | null =>
  useContext(ReportThemeContext);

export interface ReportThemeProviderProps {
  theme?: Partial<ReportTheme>;
  className?: string;
  children: ReactNode;
}

/** Applies report theme tokens as CSS variables on a wrapper. */
export const ReportThemeProvider = ({
  theme,
  className,
  children,
}: ReportThemeProviderProps) => {
  const resolved: ReportTheme = {
    ...DEFAULT_REPORT_THEME,
    ...theme,
  };

  const style = {
    [REPORT_CSS_VAR_PRIMARY]: resolved.primaryColor,
    ...(resolved.chartPalette
      ? {
          "--chart-1": resolved.chartPalette[0],
          "--chart-2": resolved.chartPalette[1],
          "--chart-3": resolved.chartPalette[2],
          "--chart-4": resolved.chartPalette[3],
          "--chart-5": resolved.chartPalette[4],
        }
      : {}),
    fontFamily: resolved.fontFamily ?? undefined,
  } as CSSProperties;

  return (
    <ReportThemeContext.Provider value={resolved}>
      <div className={cn("text-foreground", className)} style={style}>
        {children}
      </div>
    </ReportThemeContext.Provider>
  );
};
